import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Badge } from '@antimatterhq/ui/components/badge';
import {
  Pagination,
  PaginationContent,
  PaginationNext,
  PaginationPrevious,
} from '@antimatterhq/ui/components/pagination';
import { produce } from 'immer';
import {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableHead,
  SimpleTableBody,
  SimpleTableSkeletonRows,
  SimpleTableCell,
} from '@antimatterhq/ui/components/simpleTable';
import Table from '@antimatterhq/ui/components/table';
import { usePaginationManager } from '@/hooks/components/usePaginationManager';
import {
  useGetObservablesKind,
  useOptimisticUpdateObservablesKind,
} from '@/hooks/dbui/useGetObservablesKind';
import { ObservableItem } from '@/components/observables/table/BaseObservablesTable';
import ModifyRiskScoreDialog from '@/components/dialog/modifyRiskScore/ModifyRiskScoreDialog';
import { useClearCacheObservablesTop10 } from '@/hooks/dbui/useGetObservablesTop10';
import { EmptyTable } from '@/components/table/empty/EmptyTable';
import { BackLink } from '@/components/link/back/BackLink';

const formatToK = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 0,
});
const fmt = (risk?: number | null) =>
  risk !== undefined && risk !== null ? formatToK.format(risk) : risk;

export default function Observable() {
  const navigate = useNavigate();
  const { kind } = useParams();
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetObservablesKind({ kind });
  const optimisticUpdate = useOptimisticUpdateObservablesKind({ kind });
  const clearTop10Cache = useClearCacheObservablesTop10();
  const [editItem, setEditItem] = useState<
    { value: string; riskScore: number } | undefined
  >(undefined);

  const {
    page,
    goToNextPage,
    goToPreviousPage,
    nextPageEnabled,
    previousPageEnabled,
  } = usePaginationManager({
    fetchNextPage,
    hasNextPage,
    pages: data?.pages || [],
    isFetchingNextPage,
  });

  const modifyRiskScore = useCallback(
    async ({ value, riskScore }: { value: string; riskScore: number }) => {
      const result = produce(data, (draft) => {
        draft?.pages?.forEach((p) =>
          p.items?.forEach((row) => {
            if (row.value === value) {
              // eslint-disable-next-line no-param-reassign
              row.riskScore = riskScore;
            }
          }),
        );
      });
      optimisticUpdate(result);
      clearTop10Cache();
      setEditItem(undefined);
    },
    [optimisticUpdate, data, clearTop10Cache],
  );

  const handleEdit = (item: ObservableItem) => {
    setEditItem({
      value: item.value || '',
      riskScore: item.riskScore || 0,
    });
  };

  const handleView = (item: ObservableItem) => {
    navigate(`/explore/observables/${kind}/${item.value}/events`);
  };

  const items = useMemo(() => {
    return page?.items || [];
  }, [page]);

  const loading = !items.length && (isPending || isFetchingNextPage);

  return (
    <>
      <BackLink fallbackRoute="/explore/observables" />

      <Spacer size="base" />

      <Text size="lg">All {kind} by risk</Text>

      <Spacer size="base" />

      <SimpleTable className="w-full" fixed>
        <SimpleTableHeader>
          <SimpleTableRow>
            <SimpleTableHead className="w-auto truncate font-bold">
              {kind}
            </SimpleTableHead>
            <SimpleTableHead className="w-[100px] font-bold">
              Risk score
            </SimpleTableHead>
            <SimpleTableHead className="w-[50px]"></SimpleTableHead>
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {loading && <SimpleTableSkeletonRows cols={3} rows={10} />}
          {!loading &&
            items.map((item) => (
              <SimpleTableRow
                key={`${kind}-${item.value}`}
                className="h-[35px]"
              >
                <SimpleTableCell className="w-auto truncate">
                  {item.value}
                </SimpleTableCell>
                <SimpleTableCell className="w-[100px]">
                  <Badge variant="filled" color="info">
                    {fmt(item.riskScore)}
                  </Badge>
                </SimpleTableCell>
                <SimpleTableCell className="relative w-[50px]">
                  <Table.ActionsCell
                    data-testid={item.value || ''}
                    actions={[
                      {
                        label: 'Modify risk score',
                        onClick: () => handleEdit(item),
                        icon: 'antimatterhq:pencil',
                      },
                      {
                        label: 'See events',
                        onClick: () => handleView(item),
                        icon: 'lucide:list',
                      },
                    ]}
                  />
                </SimpleTableCell>
              </SimpleTableRow>
            ))}
          {!loading && !items.length && (
            <EmptyTable message="No observables here" cols={3} />
          )}
        </SimpleTableBody>
      </SimpleTable>

      <Spacer size="base" />

      {!loading && !!items.length && (
        <Pagination>
          <PaginationContent>
            <PaginationPrevious
              disabled={!previousPageEnabled}
              onClick={goToPreviousPage}
            />
            <PaginationNext
              disabled={!nextPageEnabled}
              onClick={goToNextPage}
            />
          </PaginationContent>
        </Pagination>
      )}

      <ModifyRiskScoreDialog
        kind={kind || ''}
        isOpen={!!editItem}
        onCancel={() => setEditItem(undefined)}
        value={editItem?.value || ''}
        riskScore={editItem?.riskScore || 0}
        onSave={(riskScore) =>
          modifyRiskScore({ value: editItem?.value || '', riskScore })
        }
      />
    </>
  );
}
